import { useEffect, useState, useTransition } from "react";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/card/BackLink";
import { useScreenLabel } from "@/lib/screenTrail";
import { Skeleton } from "@/components/feedback/Skeleton";
import { TaskDetailTabs } from "@/features/tasks/TaskDetailTabs";
import { TaskAttachments } from "@/features/tasks/TaskAttachments";
import { EscalateTaskModal } from "@/features/tasks/EscalateTaskModal";
import { MergeTaskDialog } from "@/features/tasks/MergeTaskDialog";
import { PlanTaskTimeDialog } from "@/features/tasks/PlanTaskTimeDialog";
import { ParticipantsField } from "@/features/tasks/ParticipantsField";
import { OrderPicker } from "@/features/orders/OrderPicker";
import {
  StatusBadge,
  STATUS_LABEL_KEYS,
  PRIORITY_LABEL_KEYS,
  TASK_STATUSES,
  TASK_PRIORITIES,
  formatDate,
} from "@/features/tasks/TaskDrawer";
import {
  getTask,
  listTaskCategories,
  updateTask,
  unplanTaskTime,
  addTaskParticipant,
  removeTaskParticipant,
  tasksKeys,
  type TaskUserRef,
  type TaskPriority,
  type TaskStatus,
  type UpdateTaskInput,
} from "@/features/tasks/tasks.api";
import { listMailboxes, mailKeys } from "@/features/mail/mail.api";
import { markTaskNotificationsRead, notificationKeys } from "@/features/notifications/notifications.api";
import { hasModule, firstAllowedRoute } from "@/lib/auth";

export const Route = createFileRoute("/_shell/tasks/$id")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "tasks")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  component: TaskDetailScreen,
});

function TaskDetailScreen() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { session } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const canCalendar = hasModule(session, "calendar");
  // Who can open an order can follow a task to it, and hang one on an order (docs/tasks.md § Taken bij een order).
  const canOrders = hasModule(session, "tms_orders") || hasModule(session, "tms_orders_write");

  const taskQuery = useQuery({
    queryKey: tasksKeys.detail(id),
    queryFn: () => getTask(id),
    retry: false,
  });
  useScreenLabel(taskQuery.data ? (taskQuery.data.number ?? taskQuery.data.title) : null);
  const mailboxesQuery = useQuery({
    queryKey: mailKeys.mailboxes,
    queryFn: () => listMailboxes(),
  });
  // The task decides which list of kenmerken applies, so this waits for it.
  const taskMailboxId = taskQuery.data?.mailbox?.id ?? null;
  const categoriesQuery = useQuery({
    queryKey: tasksKeys.categories(taskMailboxId),
    queryFn: () => listTaskCategories(false, taskMailboxId),
    enabled: taskQuery.data !== undefined,
  });

  // Opening a task clears its "reactie"-badge: mark this task's notifications
  // read, then refresh the list + bell so the badge/counter drop.
  useEffect(() => {
    markTaskNotificationsRead(id)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["tasks"] });
        queryClient.invalidateQueries({ queryKey: notificationKeys.unread });
        queryClient.invalidateQueries({ queryKey: notificationKeys.list });
      })
      .catch(() => {
        /* non-fatal — the badge clears on the next list refresh */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function refresh() {
    queryClient.invalidateQueries({ queryKey: tasksKeys.detail(id) });
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
  }

  function applyUpdate(input: UpdateTaskInput) {
    setError(null);
    startTransition(async () => {
      try {
        await updateTask(id, input);
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : t("tasks.errorGeneric"));
      }
    });
  }

  if (taskQuery.isPending) {
    return (
      <div className="wl-page" data-screen-label="Back-office Task Detail" aria-busy="true">
        <div style={{ marginBottom: "28px" }}>
          <div className="wl-hstack" style={{ gap: "12px", alignItems: "center", marginBottom: "10px" }}>
            <Skeleton width="280px" height="var(--space-7)" />
            <Skeleton width="96px" height="var(--space-6)" />
          </div>
          <Skeleton width="40%" height="var(--space-3)" />
        </div>
        <Skeleton height="320px" />
      </div>
    );
  }

  if (taskQuery.isError || !taskQuery.data) {
    return (
      <div className="wl-page" data-screen-label="Back-office Task Detail">
        <h1 className="t-h2">{t("tasks.notFound")}</h1>
        <p className="t-body-sm t-muted" style={{ marginTop: "4px" }}>
          {t("tasks.notFoundDesc")}
        </p>
        <Link to="/tasks" className="wl-btn wl-btn--secondary wl-btn--sm" style={{ marginTop: "16px" }}>
          {t("tasks.backToOverview")}
        </Link>
      </div>
    );
  }

  const task = taskQuery.data;

  // Who has to close this one: the person who FIRST handed it out. Read from
  // the timeline the detail already carries, which is exactly what the server
  // derives it from, so the banner cannot promise something the close would
  // refuse.
  const firstEscalation = task?.activities.find((a) => a.type === "escalated") ?? null;
  const reviewerId = firstEscalation?.actor?.id ?? null;
  const reviewerName = firstEscalation?.actor?.displayName ?? null;
  const finishedBy =
    task?.activities
      .filter((a) => a.type === "status_changed" && (a.payload as { to?: string } | null)?.to === "review")
      .at(-1)?.actor?.displayName ?? null;

  const mailbox = task.mailbox
    ? (mailboxesQuery.data ?? []).find((m) => m.id === task.mailbox!.id) ?? null
    : null;
  const dueDateInput = task.dueDate ? task.dueDate.slice(0, 10) : "";

  function startEdit() {
    setEditTitle(task.title);
    setEditDesc(task.description ?? "");
    setEditing(true);
  }
  function saveEdit() {
    const title = editTitle.trim();
    if (title.length < 2) {
      setError(t("tasks.editTitleTooShort"));
      return;
    }
    applyUpdate({ title, description: editDesc.trim() || null });
    setEditing(false);
  }
  function unplan() {
    setError(null);
    startTransition(async () => {
      try {
        await unplanTaskTime(id);
        queryClient.invalidateQueries({ queryKey: ["calendar"] });
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : t("tasks.errorGeneric"));
      }
    });
  }

  // Betrokkenen are saved one change at a time, like every other field here.
  function addParticipant(user: TaskUserRef) {
    setError(null);
    startTransition(async () => {
      try {
        await addTaskParticipant(id, user.id);
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : t("tasks.errorGeneric"));
      }
    });
  }
  function removeParticipant(user: TaskUserRef) {
    setError(null);
    // Taking yourself off can take the task out of your sight altogether: then
    // the overview is where you go, not a "taak niet gevonden" page.
    const me = session.userId;
    const seesItAnyway =
      (task.mailbox === null && task.createdBy.id === me) ||
      task.assignee?.id === me ||
      (mailbox?.members ?? []).some((m) => m.id === me && m.isActive);
    startTransition(async () => {
      try {
        await removeTaskParticipant(id, user.id);
        if (user.id === me && !seesItAnyway) {
          queryClient.invalidateQueries({ queryKey: ["tasks"] });
          navigate({ to: "/tasks" });
          return;
        }
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : t("tasks.errorGeneric"));
      }
    });
  }

  return (
    <div className="wl-page" data-screen-label="Back-office Task Detail">

      {escalateOpen && (
        <EscalateTaskModal
          task={task}
          currentUserId={session.userId}
          onClose={() => setEscalateOpen(false)}
          onEscalated={() => {
            setEscalateOpen(false);
            refresh();
          }}
        />
      )}

      {mergeOpen && (
        <MergeTaskDialog
          task={task}
          onClose={() => setMergeOpen(false)}
          onMerged={(survivor) => {
            setMergeOpen(false);
            queryClient.invalidateQueries({ queryKey: ["tasks"] });
            navigate({ to: "/tasks/$id", params: { id: survivor.id } });
          }}
        />
      )}

      {planOpen && (
        <PlanTaskTimeDialog
          task={task}
          onClose={() => setPlanOpen(false)}
          onPlanned={() => {
            setPlanOpen(false);
            queryClient.invalidateQueries({ queryKey: ["calendar"] });
            refresh();
          }}
        />
      )}

      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        {/* Back to the list AS YOU LEFT IT — the breadcrumb's job before the
            navigation moved to the top. A bare <Link to="/tasks"> resets every
            filter, so opening a task from "Mijn taken" and going back dropped
            you on "Alles" — see lib/listState. */}
        <BackLink list="/tasks" listLabel={t("nav.tasks")} style={{ marginBottom: "var(--space-2)" }} />
        <div className="wl-hstack" style={{ gap: "12px", alignItems: "center", marginBottom: "6px" }}>
          {task.number && (
            <span className="t-mono" style={{ fontSize: "13px", fontWeight: "var(--fw-medium)", color: "var(--text-navy)" }}>
              {task.number}
            </span>
          )}
          {editing ? (
            <input
              className="wl-input"
              style={{ margin: 0, fontSize: "var(--fs-lg)", fontWeight: "var(--fw-semibold)", minWidth: "320px", flex: 1 }}
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              disabled={isPending}
              aria-label={t("tasks.titleLabel")}
            />
          ) : (
            <h1 className="t-h2" style={{ margin: 0 }}>{task.title}</h1>
          )}
          <StatusBadge status={task.status} />
          {task.recurrenceId && (
            <span
              className="t-caption"
              style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--text-secondary)" }}
              title={t("tasks.recurringBadgeHelp")}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17 2l4 4-4 4" /><path d="M3 11v-1a4 4 0 0 1 4-4h14" />
                <path d="M7 22l-4-4 4-4" /><path d="M21 13v1a4 4 0 0 1-4 4H3" />
              </svg>
              {t("tasks.recurringBadge")}
            </span>
          )}
          {task.calendarEventId && (
            <span
              className="t-caption"
              style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--text-brand)" }}
              title={t("tasks.plannedBadgeHelp")}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
              </svg>
              {t("tasks.plannedBadge")}
            </span>
          )}
          <div className="wl-spacer" />
          {editing ? (
            <>
              <button className="wl-btn wl-btn--accent" onClick={saveEdit} disabled={isPending}>{t("common.save")}</button>
              <button className="wl-btn wl-btn--secondary" onClick={() => setEditing(false)} disabled={isPending}>{t("common.cancel")}</button>
            </>
          ) : (
            <>
              {!task.mergedIntoId && (
                <button className="wl-btn wl-btn--secondary" onClick={startEdit} title={t("tasks.editButtonHelp")}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                  {t("tasks.editButton")}
                </button>
              )}
              {canCalendar && !task.mergedIntoId && task.status !== "done" && task.status !== "cancelled" && (
                <button className="wl-btn wl-btn--secondary" onClick={() => setPlanOpen(true)} title={t("tasks.planTimeHelp")}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M3 10h18M8 2v4M16 2v4" />
                  </svg>
                  {task.calendarEventId ? t("tasks.planTimeAgainButton") : t("tasks.planTimeButton")}
                </button>
              )}
              {canCalendar && task.calendarEventId && !task.mergedIntoId && (
                <button className="wl-btn wl-btn--ghost" onClick={unplan} disabled={isPending} title={t("tasks.unplanHelp")}>
                  {t("tasks.unplanButton")}
                </button>
              )}
              {!task.mergedIntoId && (
                <button className="wl-btn wl-btn--secondary" onClick={() => setMergeOpen(true)} title={t("tasks.mergeHelp")}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M7 3v6a5 5 0 0 0 5 5 5 5 0 0 0 5-5V3" /><path d="M12 14v7" /><path d="m9 18 3 3 3-3" />
                  </svg>
                  {t("tasks.mergeButton")}
                </button>
              )}
              {!task.mergedIntoId && task.status !== "done" && task.status !== "cancelled" && (
                <button className="wl-btn wl-btn--secondary" onClick={() => setEscalateOpen(true)}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="15 14 20 9 15 4" />
                    <path d="M4 20v-7a4 4 0 0 1 4-4h12" />
                  </svg>
                  {t("tasks.escalateButton")}
                </button>
              )}
            </>
          )}
        </div>
        <div className="wl-hstack t-body-sm t-muted" style={{ gap: "16px" }}>
          <span>
            {task.mailbox
              ? `${task.mailbox.displayName} (${task.mailbox.email})`
              : t("tasks.personal")}
          </span>
          <span>·</span>
          <span>{t("tasks.createdByLabel", { name: task.createdBy.displayName, date: formatDate(task.createdAt) })}</span>
          {!task.mailbox && task.assignee && (
            <>
              <span>·</span>
              <span>{t("tasks.assignedToLabel", { name: task.assignee.displayName })}</span>
            </>
          )}
          {task.order && (
            <>
              <span>·</span>
              <span>
                {t("tasks.orderLabel")}{" "}
                {canOrders ? (
                  <Link to="/orders/$id" params={{ id: task.order.id }} className="t-mono" style={{ color: "var(--text-brand)" }}>
                    {task.order.number}
                  </Link>
                ) : (
                  <span className="t-mono">{task.order.number}</span>
                )}{" "}
                · {task.order.customerName}
              </span>
            </>
          )}
        </div>
        {task.mergedFrom.length > 0 && (
          <div className="t-caption t-muted" style={{ marginTop: "6px" }}>
            {t("tasks.mergedFromLabel")}{" "}
            {task.mergedFrom.map((m, i) => (
              <span key={m.id}>
                {i > 0 && ", "}
                <Link to="/tasks/$id" params={{ id: m.id }} style={{ color: "var(--text-brand)" }}>
                  {m.number ?? t("tasks.openTaskFallback")}
                </Link>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Somebody finished a task you handed out. Not a gate and not a chore:
          nobody is obliged to press "afgerond" in the first place — this only
          catches the case where they do, so the task does not quietly leave
          your list (Camiel, 2026-09-02). Closing it is one click. */}
      {task.status === "review" && (
        <div className="wl-alert wl-alert--info" style={{ marginBottom: "16px" }}>
          {reviewerId === session.userId ? (
            <>
              <span>{t("tasks.reviewBanner", { actor: finishedBy ?? t("notif.someone") })}</span>
              <button
                className="wl-btn wl-btn--primary wl-btn--sm"
                style={{ marginLeft: "auto" }}
                onClick={() => applyUpdate({ status: "done" })}
                disabled={isPending}
              >
                {t("tasks.reviewClose")}
              </button>
              <button
                className="wl-btn wl-btn--secondary wl-btn--sm"
                onClick={() => applyUpdate({ status: "in_progress" })}
                disabled={isPending}
              >
                {t("tasks.reviewReopen")}
              </button>
            </>
          ) : (
            <span>{t("tasks.reviewWaiting", { name: reviewerName ?? t("notif.someone") })}</span>
          )}
        </div>
      )}

      {task.mergedIntoId && task.mergedInto && (
        <div className="wl-alert wl-alert--info" style={{ marginBottom: "16px" }}>
          <span>
            {t("tasks.mergedBanner", { number: task.mergedInto.number ?? t("tasks.openTaskFallback") })}{" "}
            <Link to="/tasks/$id" params={{ id: task.mergedInto.id }} style={{ fontWeight: "var(--fw-semibold)" }}>
              {t("tasks.mergedBannerLink")}
            </Link>
          </span>
        </div>
      )}

      {error && (
        <div className="wl-alert wl-alert--danger" style={{ marginBottom: "16px" }}>
          <span>{error}</span>
        </div>
      )}

      {/* Controls + description */}
      <div className="wl-card" style={{ marginBottom: "24px", padding: "20px 24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 230px))", gap: "20px" }}>
          <div className="wl-field">
            <label className="wl-field__label" htmlFor="task-status">{t("tasks.statusLabel")}</label>
            <select
              id="task-status"
              className="wl-select"
              value={task.status}
              disabled={isPending}
              onChange={(e) => applyUpdate({ status: e.target.value as TaskStatus })}
            >
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>{t(STATUS_LABEL_KEYS[s] as never)}</option>
              ))}
            </select>
          </div>

          {task.mailbox && (
            <div className="wl-field">
              <label className="wl-field__label" htmlFor="task-assignee">{t("tasks.assigneeLabel")}</label>
              <select
                id="task-assignee"
                className="wl-select"
                value={task.assignee?.id ?? ""}
                disabled={isPending}
                onChange={(e) => applyUpdate({ assigneeId: e.target.value || null })}
              >
                <option value="">{t("tasks.assigneeNone")}</option>
                {(mailbox?.members ?? []).filter((m) => m.isActive).map((m) => (
                  <option key={m.id} value={m.id}>{m.displayName}</option>
                ))}
                {/* An escalated task may be assigned outside the members. */}
                {task.assignee && !(mailbox?.members ?? []).some((m) => m.id === task.assignee!.id) && (
                  <option value={task.assignee.id}>{task.assignee.displayName}</option>
                )}
              </select>
            </div>
          )}

          <div className="wl-field">
            <label className="wl-field__label" htmlFor="task-priority">{t("tasks.priorityLabel")}</label>
            <select
              id="task-priority"
              className="wl-select"
              value={task.priority}
              disabled={isPending}
              onChange={(e) => applyUpdate({ priority: e.target.value as TaskPriority })}
            >
              {TASK_PRIORITIES.map((p) => (
                <option key={p} value={p}>{t(PRIORITY_LABEL_KEYS[p] as never)}</option>
              ))}
            </select>
          </div>

          <div className="wl-field">
            <label className="wl-field__label" htmlFor="task-category">{t("tasks.categoryLabel")}</label>
            <select
              id="task-category"
              className="wl-select"
              value={task.category?.id ?? ""}
              disabled={isPending}
              onChange={(e) => applyUpdate({ categoryId: e.target.value || null })}
            >
              <option value="">{t("tasks.categoryNone")}</option>
              {(categoriesQuery.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              {task.category && !(categoriesQuery.data ?? []).some((c) => c.id === task.category!.id) && (
                <option value={task.category.id}>{task.category.name}</option>
              )}
            </select>
          </div>

          {/* The order it is about: hang it on one, or let go of it. */}
          {canOrders && !task.mergedIntoId && (
            <div className="wl-field">
              <label className="wl-field__label" htmlFor="task-order">{t("tasks.orderLabel")}</label>
              {task.order ? (
                <div className="wl-hstack" style={{ gap: "var(--space-2)", alignItems: "center", minHeight: "var(--control-height-md)" }}>
                  <Link to="/orders/$id" params={{ id: task.order.id }} className="t-mono t-body-sm" style={{ color: "var(--text-brand)" }}>
                    {task.order.number}
                  </Link>
                  <button
                    type="button"
                    className="wl-btn wl-btn--link wl-btn--sm"
                    style={{ padding: 0 }}
                    disabled={isPending}
                    onClick={() => applyUpdate({ orderId: null })}
                  >
                    {t("tasks.orderUnlink")}
                  </button>
                </div>
              ) : (
                <OrderPicker id="task-order" disabled={isPending} onPick={(picked) => applyUpdate({ orderId: picked.id })} />
              )}
            </div>
          )}

          <div className="wl-field">
            <label className="wl-field__label" htmlFor="task-due">{t("tasks.dueDateLabel")}</label>
            <input
              id="task-due"
              className="wl-input"
              type="date"
              value={dueDateInput}
              disabled={isPending}
              onChange={(e) =>
                applyUpdate({
                  dueDate: e.target.value ? new Date(`${e.target.value}T12:00:00`).toISOString() : null,
                })
              }
            />
          </div>
        </div>

        {/* Who else is on it, next to the one opvolger. A row of its own: a list
            of names does not fit the grid of single-value controls above. */}
        <div className="wl-field" style={{ marginTop: "20px", maxWidth: "640px" }}>
          <label className="wl-field__label" htmlFor="task-participants">{t("tasks.participantsLabel")}</label>
          <ParticipantsField
            id="task-participants"
            participants={task.participants}
            excludeIds={task.assignee ? [task.assignee.id] : []}
            disabled={!!task.mergedIntoId}
            onAdd={addParticipant}
            onRemove={removeParticipant}
          />
          <span className="wl-field__help">{t("tasks.participantsHelp")}</span>
        </div>

        {editing ? (
          <div style={{ marginTop: "20px" }}>
            <div className="t-caption" style={{ marginBottom: "4px" }}>{t("tasks.descriptionLabel")}</div>
            <textarea
              className="wl-input"
              rows={4}
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              disabled={isPending}
              placeholder={t("tasks.descriptionPlaceholder")}
            />
          </div>
        ) : task.description ? (
          <div style={{ marginTop: "20px" }}>
            <div className="t-caption" style={{ marginBottom: "4px" }}>{t("tasks.descriptionLabel")}</div>
            <div className="t-body-sm" style={{ whiteSpace: "pre-wrap" }}>{task.description}</div>
          </div>
        ) : null}
        <TaskAttachments task={task} onChanged={refresh} />
      </div>

      <TaskDetailTabs task={task} onChanged={refresh} />
    </div>
  );
}
